import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle, Wallet, CreditCard, MapPin, Loader2 } from "lucide-react";

// Add newline here to separate external from internal
import CheckoutForm from "../components/P2P/CheckoutForm";
import { useWallet } from "../contexts/WalletContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";

function CheckoutPage() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const {
    isConnected: walletConnected,
    connectWallet,
    fetchAddresses,
    fetchPaymentMethods,
    executePurchase,
    addresses,
    paymentMethods,
  } = useWallet();

  const [clientSecret, setClientSecret] = useState(null);
  const [listingDetails, setListingDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);

  // Payment method selection: "wallet" or "stripe"
  const [paymentMode, setPaymentMode] = useState("stripe");
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [isProcessingWallet, setIsProcessingWallet] = useState(false);

  useEffect(() => {
    const initiateCheckout = async () => {
      setIsLoading(true);
      setError(null);
      setIsSuccess(false); // Reset success state on load
      console.log(`Initiating checkout for listing: ${listingId}`);
      try {
        const response = await fetch(`/api/secure/p2p/checkout/${listingId}`, {
          method: "POST",
          // Auth headers added automatically?
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || "Failed to initiate checkout");
        }

        const result = await response.json();
        if (result.success && result.clientSecret) {
          setClientSecret(result.clientSecret);
          // Fetch listing details needed for display (e.g., title, price)
          // This should ideally happen BEFORE showing the form
          // For now, we'll simulate it
          setListingDetails({
            id: listingId,
            title: "Fetched Item Title",
            price: "29.99",
          }); // Placeholder
        } else {
          throw new Error(result.error || "Failed to get client secret");
        }
      } catch (err) {
        console.error("Checkout initiation error:", err);
        setError(err.message);
      }
      setIsLoading(false);
    };

    if (listingId) {
      initiateCheckout();
    } else {
      // Handle case where listingId is missing?
      setError("Invalid listing ID.");
      setIsLoading(false);
    }
  }, [listingId]);

  // Load wallet data when connected
  useEffect(() => {
    if (walletConnected) {
      fetchAddresses();
      fetchPaymentMethods();
    }
  }, [walletConnected, fetchAddresses, fetchPaymentMethods]);

  // Set defaults when wallet data loads
  useEffect(() => {
    if (addresses.length > 0 && !selectedAddress) {
      const defaultAddr = addresses.find(a => a.is_default) || addresses[0];
      setSelectedAddress(defaultAddr);
    }
  }, [addresses, selectedAddress]);

  useEffect(() => {
    if (paymentMethods.length > 0 && !selectedPaymentMethod) {
      const defaultPm = paymentMethods.find(p => p.is_default) || paymentMethods[0];
      setSelectedPaymentMethod(defaultPm);
    }
  }, [paymentMethods, selectedPaymentMethod]);

  const handleWalletPayment = async () => {
    if (!selectedAddress || !selectedPaymentMethod || !listingDetails) {
      setError("Please select an address and payment method");
      return;
    }

    setIsProcessingWallet(true);
    setError(null);

    try {
      const purchaseData = {
        productId: listingDetails.id,
        productTitle: listingDetails.title,
        productPrice: Math.round(parseFloat(listingDetails.price) * 100),
        productCurrency: "GBP",
        listingId: listingId,
        addressId: selectedAddress.id,
        paymentMethodId: selectedPaymentMethod.id,
        correlationId: `checkout_${Date.now()}`,
      };

      const result = await executePurchase(purchaseData);

      if (result.success) {
        setIsSuccess(true);
        setTimeout(() => navigate("/"), 3000);
      } else {
        throw new Error(result.error?.message || "Purchase failed");
      }
    } catch (err) {
      setError(`Payment failed: ${err.message}`);
    } finally {
      setIsProcessingWallet(false);
    }
  };

  const handlePaymentSuccess = (paymentIntent) => {
    console.log("Payment Successful!", paymentIntent);
    setIsSuccess(true); // Show success message
    setError(null);
    // Redirect after a short delay
    setTimeout(() => {
      navigate("/"); // Redirect home (or to order details page)
    }, 3000); // 3 second delay
  };

  const handlePaymentError = (errorMessage) => {
    console.error("Payment Failed:", errorMessage);
    setError(`Payment failed: ${errorMessage}`);
    setIsSuccess(false); // Ensure success message is hidden
  };

  return (
    <div className="p-4 md:p-8 max-w-md mx-auto">
      <h1 className="text-xl font-semibold mb-4 text-center">Checkout</h1>

      {/* Loading State */}
      {isLoading && <p className="text-center">Loading checkout...</p>}

      {/* Success State */}
      {isSuccess && (
        <div className="text-center p-6 bg-green-50 border border-green-200 rounded-md">
          <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
          <h2 className="text-lg font-semibold text-green-700">
            Payment Successful!
          </h2>
          <p className="text-sm text-green-600 mt-1">
            Thank you for your purchase. You will be redirected shortly.
          </p>
        </div>
      )}

      {/* Error State (Only show if not success) */}
      {error && !isSuccess && (
        <p className="text-center text-red-500 bg-red-50 p-3 border border-red-200 rounded-md">
          Error: {error}
        </p>
      )}

      {/* Payment Method Selection */}
      {!isLoading && !error && !isSuccess && clientSecret && (
        <div className="space-y-4">
          {/* Payment Mode Tabs */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={paymentMode === "wallet" ? "default" : "outline"}
              onClick={() => setPaymentMode("wallet")}
              className="flex-1 flex items-center justify-center gap-2"
              disabled={!walletConnected}
            >
              <Wallet size={18} />
              Thingdom Wallet
            </Button>
            <Button
              variant={paymentMode === "stripe" ? "default" : "outline"}
              onClick={() => setPaymentMode("stripe")}
              className="flex-1 flex items-center justify-center gap-2"
            >
              <CreditCard size={18} />
              Card Payment
            </Button>
          </div>

          {/* Connect Wallet Prompt */}
          {!walletConnected && paymentMode === "stripe" && (
            <Card className="mb-4 border-blue-200 bg-blue-50">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <Wallet className="text-blue-600 mt-0.5" size={20} />
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      Connect your Thingdom Wallet for faster checkout
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      Use saved addresses and payment methods
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={connectWallet}
                      className="mt-2 text-blue-700 border-blue-300 hover:bg-blue-100"
                    >
                      Connect Wallet
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Wallet Payment Form */}
          {paymentMode === "wallet" && walletConnected && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wallet size={20} />
                  Pay with Thingdom Wallet
                </CardTitle>
                <CardDescription>
                  Complete your purchase using saved details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Address Selection */}
                <div>
                  <label htmlFor="address-select" className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                    <MapPin size={16} />
                    Shipping Address
                  </label>
                  {addresses.length === 0 ? (
                    <p className="text-sm text-gray-500">No saved addresses</p>
                  ) : (
                    <select
                      id="address-select"
                      value={selectedAddress?.id || ""}
                      onChange={(e) => {
                        const addr = addresses.find(a => a.id === e.target.value);
                        setSelectedAddress(addr);
                      }}
                      className="w-full p-2 border rounded-md text-sm"
                    >
                      {addresses.map((addr) => (
                        <option key={addr.id} value={addr.id}>
                          {addr.label}: {addr.line1}, {addr.city}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Payment Method Selection */}
                <div>
                  <label htmlFor="payment-select" className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                    <CreditCard size={16} />
                    Payment Method
                  </label>
                  {paymentMethods.length === 0 ? (
                    <p className="text-sm text-gray-500">No saved payment methods</p>
                  ) : (
                    <select
                      id="payment-select"
                      value={selectedPaymentMethod?.id || ""}
                      onChange={(e) => {
                        const pm = paymentMethods.find(p => p.id === e.target.value);
                        setSelectedPaymentMethod(pm);
                      }}
                      className="w-full p-2 border rounded-md text-sm"
                    >
                      {paymentMethods.map((pm) => (
                        <option key={pm.id} value={pm.id}>
                          {pm.type === "wallet_balance"
                            ? `Pentatonic Balance (${pm.balance?.display || "£0.00"})`
                            : `${pm.card?.brand?.toUpperCase() || "Card"} •••• ${pm.card?.last4}`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Order Summary */}
                {listingDetails && (
                  <div className="bg-gray-50 p-3 rounded-md">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Item</span>
                      <span className="font-medium">{listingDetails.title}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-600">Total</span>
                      <span className="font-medium">£{listingDetails.price}</span>
                    </div>
                  </div>
                )}

                {/* Pay Button */}
                <Button
                  onClick={handleWalletPayment}
                  disabled={isProcessingWallet || !selectedAddress || !selectedPaymentMethod}
                  className="w-full"
                >
                  {isProcessingWallet ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={18} />
                      Processing...
                    </>
                  ) : (
                    <>Pay £{listingDetails?.price || "0.00"}</>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Stripe Form (existing) */}
          {paymentMode === "stripe" && (
            <CheckoutForm
              listing={listingDetails}
              clientSecret={clientSecret}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentError={handlePaymentError}
            />
          )}
        </div>
      )}

      {/* Fallback if checkout couldn't load (and not already handled by other states) */}
      {!isLoading && !error && !isSuccess && !clientSecret && (
        <p className="text-center text-gray-500">
          Could not load checkout session.
        </p>
      )}
    </div>
  );
}

export default CheckoutPage;
